// ============================================================================
// JARVISTRAVEL - FATIGUE INDEX (FI) — shared scale helpers
// Single source of truth for the public 1–9 FI: band, tone (token classes),
// and human descriptor. Used by every FI surface so the colors + language are
// consistent. Scale (FIS V4): 1–3 Chill · 4–6 Balanced · 7–9 Packed.
// ============================================================================

export type FIBand = 'chill' | 'balanced' | 'packed';

export const FI_MIN = 1;
export const FI_MAX = 9;

/** Clamp/round any value into the public 1–9 range. Broken upstream data
 *  (NaN — including a runtime `undefined` coerced by Math.round) clamps to the
 *  calm floor instead of poisoning every FI surface downstream; ±Infinity
 *  clamps to the scale bounds via min/max. */
export const clampFi = (score: number): number => {
  const rounded = Math.round(score);
  return Number.isNaN(rounded) ? FI_MIN : Math.min(FI_MAX, Math.max(FI_MIN, rounded));
};

/** Band (the three plain-language tiers): 1–3 Chill · 4–6 Balanced · 7–9 Packed. */
export function fiBand(score: number): FIBand {
  const s = clampFi(score);
  if (s <= 3) return 'chill';
  if (s <= 6) return 'balanced';
  return 'packed';
}

export const fiBandLabel = (band: FIBand): string =>
  band === 'chill' ? 'Chill' : band === 'balanced' ? 'Balanced' : 'Packed';

/** Short, calm descriptor (FIS spec §6.1) — never judgmental. */
export const fiDescriptor = (band: FIBand): string =>
  band === 'chill' ? 'easy day' : band === 'balanced' ? 'full but manageable' : 'high-effort day';

/**
 * Color key — a finer, per-score temperature step than the band. The high end
 * escalates green → amber → red so only the very top reads as an alarm:
 *   1–3 chill (blue) · 4–6 balanced (green) · 7–8 elevated (amber) · 9 packed (red).
 * Band stays 3-way (7–9 are all "Packed"); only the color granularity is finer.
 */
export type FIColorKey = 'chill' | 'balanced' | 'elevated' | 'packed';

export function fiColorKey(score: number): FIColorKey {
  const s = clampFi(score);
  if (s <= 3) return 'chill';
  if (s <= 6) return 'balanced';
  if (s <= 8) return 'elevated';
  return 'packed';
}

/** Token classes for the fatigue scale, so a score reads the same on badges,
 *  pills, dots, rings, bars, and cards. */
export interface FITone { text: string; bg: string; solid: string; dot: string; ring: string; border: string; }

const TONES: Record<FIColorKey, FITone> = {
  chill:    { text: 'text-fi-chill',    bg: 'bg-fi-chill/10',    solid: 'bg-fi-chill',    dot: 'bg-fi-chill',    ring: 'ring-fi-chill/40',    border: 'border-fi-chill/30' },
  balanced: { text: 'text-fi-balanced', bg: 'bg-fi-balanced/10', solid: 'bg-fi-balanced', dot: 'bg-fi-balanced', ring: 'ring-fi-balanced/40', border: 'border-fi-balanced/30' },
  elevated: { text: 'text-fi-elevated', bg: 'bg-fi-elevated/10', solid: 'bg-fi-elevated', dot: 'bg-fi-elevated', ring: 'ring-fi-elevated/40', border: 'border-fi-elevated/30' },
  packed:   { text: 'text-fi-packed',   bg: 'bg-fi-packed/10',   solid: 'bg-fi-packed',   dot: 'bg-fi-packed',   ring: 'ring-fi-packed/40',   border: 'border-fi-packed/30' },
};

/** Tone for an exact 1–9 score — the preferred accessor (carries the 7–8 amber
 *  / 9 red distinction). Use this wherever a score is available. */
export const fiToneForScore = (score: number): FITone => TONES[fiColorKey(score)];

/** Tone for a band only (no exact score) — maps to the band's representative
 *  color (Packed → red). Prefer fiToneForScore when a score is known. */
export const fiTone = (band: FIBand): FITone => TONES[band];
